import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Users } from 'lucide-react';

/**
 * Display status colors for the sidebar dots
 */
const displayStatusColors = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500'
};

const displayStatusLabels = {
  green: 'Active',
  yellow: 'Idle',
  red: 'Offline'
};

/**
 * Status dot for display status (green/yellow/red)
 */
export const DisplayStatusDot = ({ displayStatus, size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span
      className={`${sizeClasses[size]} ${displayStatusColors[displayStatus] || displayStatusColors.red} rounded-full ${className}`}
      title={displayStatusLabels[displayStatus]}
    />
  );
};

/**
 * Team presence list component for the sidebar
 * Shows all staff/admin users with their current activity status
 */
const TeamPresenceList = ({ collapsed = false }) => {
  const { isAuthenticated, isStaff, isAdmin, user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user can view team presence (staff, admin, or attorney)
  const canViewTeam = isStaff || isAdmin || user?.role === 'attorney';

  // Load team presence data
  const loadTeamPresence = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/presence/team');
      setTeamMembers(response.data.members || []);
      setError(null);
    } catch (err) {
      console.error('Error loading team presence:', err);
      // Don't show error for 403 - user just doesn't have permission
      if (err.response?.status === 403) {
        setError(null);
      } else {
        setError('Failed to load team');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load on mount and poll every 30 seconds
  useEffect(() => {
    if (!canViewTeam) {
      setLoading(false);
      return;
    }

    loadTeamPresence();
    const interval = setInterval(loadTeamPresence, 30000);

    return () => clearInterval(interval);
  }, [loadTeamPresence, canViewTeam]);

  // Don't render for non-staff users
  if (!canViewTeam) {
    return null;
  }

  // Collapsed view - just show status dots
  if (collapsed) {
    return (
      <div className="px-2 py-2 flex flex-col items-center gap-1">
        <Users size={16} className="text-virgo-teal-400 mb-1" />
        {teamMembers.slice(0, 5).map((member) => (
          <DisplayStatusDot
            key={member.user_id}
            displayStatus={member.display_status}
            size="xs"
            className="ring-1 ring-virgo-navy-800"
          />
        ))}
        {teamMembers.length > 5 && (
          <span className="text-xs text-virgo-teal-400">+{teamMembers.length - 5}</span>
        )}
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* Header */}
      <div className="px-4 py-2 text-xs font-semibold text-virgo-gold-400 uppercase tracking-wider flex items-center gap-2">
        <Users size={14} />
        Team
      </div>

      {/* Loading state */}
      {loading && (
        <div className="px-4 py-2 text-xs text-virgo-teal-400">
          Loading...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="px-4 py-2 text-xs text-red-400">
          {error}
        </div>
      )}

      {/* Team list */}
      {!loading && !error && (
        <div className="space-y-0.5 px-2">
          {teamMembers.map((member) => (
            <div
              key={member.user_id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-virgo-navy-700/50 transition-colors"
              title={member.custom_status_text || displayStatusLabels[member.display_status]}
            >
              <DisplayStatusDot
                displayStatus={member.display_status}
                size="sm"
              />
              <span className="text-sm text-virgo-teal-200 truncate flex-1">
                {member.display_name || member.username}
              </span>
              {member.custom_status_text && (
                <span className="text-xs text-virgo-teal-500 truncate max-w-[80px]">
                  {member.custom_status_text}
                </span>
              )}
            </div>
          ))}

          {teamMembers.length === 0 && (
            <div className="px-2 py-2 text-xs text-virgo-teal-500">
              No team members found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamPresenceList;
